/**
 * ProximityEngine — pure calculation, no DOM or audio dependencies.
 *
 * Takes a player position and a list of building positions.
 * Returns buildings sorted by distance, with the nearest-N within range.
 */

import * as THREE from 'three';

export interface BuildingProximity {
  trackId: string;
  position: [number, number, number];
  distance: number;
}

export class ProximityEngine {
  /** Max distance (in world units) at which a building is considered "in range" */
  maxRange: number;

  /** How many of the nearest buildings to return (for crossfade) */
  maxActive: number;

  constructor(maxRange = 10, maxActive = 2) {
    this.maxRange = maxRange;
    this.maxActive = maxActive;
  }

  /**
   * Calculate proximity for all buildings relative to a player position.
   * Returns sorted array (closest first), filtered to those within maxRange.
   */
  evaluate(
    playerPos: [number, number, number],
    buildings: Array<{ trackId: string; position: [number, number, number] }>,
  ): BuildingProximity[] {
    const playerVec = new THREE.Vector3(...playerPos);

    const results: BuildingProximity[] = buildings
      .map((b) => {
        const buildingVec = new THREE.Vector3(...b.position);
        const distance = playerVec.distanceTo(buildingVec);
        return { trackId: b.trackId, position: b.position, distance };
      })
      .filter((r) => r.distance <= this.maxRange)
      .sort((a, b) => a.distance - b.distance);

    return results.slice(0, this.maxActive);
  }

  /**
   * Calculate volume level based on distance.
   * Quadratic falloff for steeper drop: 100 at distance=0, ~25 at halfway, 0 at maxRange.
   */
  volumeForDistance(distance: number): number {
    if (distance >= this.maxRange) return 0;
    const t = 1 - distance / this.maxRange; // 1 (near) → 0 (far)
    return Math.round(100 * t * t); // quadratic
  }
}
